import { useRef, useState, useEffect, useCallback, memo, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Fab, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Cookies, withCookies } from 'react-cookie';
import Favico from 'favico.js';
import { SessionController } from '../controllers/SessionController';
import {
    IReportRequest, ILockState,
    ILinesRequest, IHelpTopic, IShowNotificationState, IRouletteRequest,
    ItemAdditionOptions,
    ITaskItem, IPenaltyItem, IGeneralSettings, MessageDialogState,
    ShowProfileProps,
    IOwnerProfileSession,
    ControlRecord,
    IProofRequest,
    ISettingsItem,
    IEventItem,
    IPermissionItem,
    IReward,
    Item,
    Task,
    AdditionDialogResponse,
    IItemAdditionDialogState,
    PermissionItem,
    TaskBasketItem,
    AuditMessage,
    IRequestLockState,
    BaseBasketItem,
    PenaltyItem,
    IAttendanceRequest,
    ITaskVoteConfig,
    BaseTimedBasketItem,
    PenaltyBasketItem,
    TaskItem,
    ITypingStats,
    TourStep,
    GameTypes,
    IGameResult,
    IQuizGame,
    IWordSearchGame,
    ICardFlipGame,
    IHigherOrLowerGame,
    IMemoryNumbersGame,
    IMinefieldGame,
    IPatternRecallGame,
    IBattleShipsGame,
    ITicTacToeGame,
    IBlackJackGame,
    IRockPaperScissorsGame,
    IFruitMachineGame,
    IRouletteGame,
    ISnakesAndLaddersGame
} from '../ts/Definitions';
import StatefulSettings from '../controllers/StatefulSettings';
import { defaultSiteEffectsConfig, SiteEffectsConfig, SiteEffectsOverlaySession } from '../components/MemberSiteEffectsFramework';
import ErrorBoundary from '../controls/ErrorBoundary';
import {
    convertToCountTypeString, findUser, getDefaultCountPerMistake, isAvailableItem, isTimedItem
} from '../utilities/utils';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { OpenInNew } from '@mui/icons-material';
import Emitter, {
    SITE_EFFECTS_TIMER_COMPLETE,
    ShowHelp, ShowMessageDialog, ShowSessionDetails, ShowProfile,
    ShowNewItemsDialog, ShowSettingItemEditorDialog, ShowNotification,
    ShowRequestPurchase, ShowAdditionDialog, ShowNewItemDialog, ShowNewTaskDialog,
    ShowPilloryDialog, ShowRewardDialog, LoanMemberDialog, TransferOwnerDialog, ShowRequestProof,
    ShowRequestForItemDialog,
    ShowLogonDialog, ShowConfirmDialog, ShowChangeScheduledTask, NOTIFY_PAGE_UPDATES, DISABLE_INSTALLER,
    ShowOptionsDialog,
    ShowTourDialog,
    ShareMemberDialog,
    ShowLovenseHelpDialog,
    ShowMetronomeDialog,
    ShowSiteEffectsOverlay,
    ShowQRCodeExplain,
    ShowTestRunDialog,
    ShowOptionPickerDialog,
    ShowImportImageDbDialog,
} from '../utilities/Emitter';
import LazyLoader from '../controls/lazyLoader';
import { SnackbarProvider, VariantType, useSnackbar } from 'notistack';
import { sendMessageToMember, MessageType, sendRequestPurchase, sendRequestProof, NotificationType } from '../controllers/messaging';
import { AuditActionTypes, CountType, EventDay, ItemTypes, PendingUpdateType, ProfileRole, TraceLevel } from '../ts/enums';
import Settings from '../controllers/settings';
import { ControllerContext, SelectedSettingsContext } from '../ts/Contexts';
import { addDays } from 'date-fns';
import { getCountMinutes } from '../utilities/utils';
import { playSound } from '../utilities/soundUtils';
import Tour from 'reactour';
import { notifications_page, ProtectedPage, settings_page, settingsManager_page, welcome_page, lovense_page, owners_page, items_page, userAudit_page, members_page, sessions_page, locks_page, pillory_page, editProfile_page, votes_page, config_page, json_page, home_page, root_page } from '../controls/ProtectedPage';
import { lazyWithRetry } from '../utilities/lazyLoaderUtils';
import { formatValidDate } from '../utilities/dateUtils';
import LoginScreen from './LoginScreen';
import MemoryProfiler from '../charts/MemoryProfiler';
import BlackJackDialog from '../dialogs/BlackJackDialog';
import RockPaperScissorsDialog from '../dialogs/RockPaperScissorsDialog';
import TicTacToeDialog from '../dialogs/TicTacToeDialog';
import BattleShipsDialog from '../dialogs/BattleShipsDialog';
import FruitMachineDialog from '../dialogs/FruitMachineDialog';
import CrapsDialog from '../dialogs/CrapsDialog';
import SnakesAndLaddersDialog from '../dialogs/SnakesAndLaddersDialog';

const QuizDialog = lazyWithRetry(() => (import('../dialogs/QuizDialog')));
const WordSearchDialog = lazyWithRetry(() => (import('../dialogs/WordSearchDialog')));
const CardFlipDialog = lazyWithRetry(() => (import('../dialogs/CardFlipDialog')));
const MemoryNumbersDialog = lazyWithRetry(() => (import('../dialogs/MemoryNumbersDialog')));
const HigherOrLowerDialog = lazyWithRetry(() => (import('../dialogs/HigherOrLowerDialog')));
const PatternRecallDialog = lazyWithRetry(() => (import('../dialogs/PatternRecallDialog')));
const MinefieldDialog = lazyWithRetry(() => (import('../dialogs/MinefieldDialog')));
const TaskOptionPicker = lazyWithRetry(() => (import('../dialogs/TaskOptionPicker')));

const RequestedPresenceDialog = lazyWithRetry(() => (import('../dialogs/RequestedPresenceDialog')));
const TaskPicker = lazyWithRetry(() => (import('../dialogs/TaskPicker')));
const TaskBasedSettingItemEditorDialog = lazyWithRetry(() => (import('../dialogs/TaskBasedSettingItemEditorDialog')));
const PositionItemViewer = lazyWithRetry(() => (import('../dialogs/positionItemViewer')));
const EventBasedSettingItemEditorDialog = lazyWithRetry(() => (import('../dialogs/EventBasedSettingItemEditorDialog')));
const ProofRequestCapture = lazyWithRetry(() => (import('../dialogs/ProofRequestCapture')));
const NewItemViewerDialog = lazyWithRetry(() => (import('../dialogs/NewItemViewerDialog')));
const RequestForItemDialog = lazyWithRetry(() => (import('../dialogs/RequestForItemDialog')));
const ActiveSessionsViewer = lazyWithRetry(() => (import('./ActiveSessionsViewer')));
const LovenseConfiguration = lazyWithRetry(() => (import('./LovenseConfiguration')));
const ReportDialog = lazyWithRetry(() => (import('../dialogs/ReportDialog')));
const LinesDialog = lazyWithRetry(() => (import('../dialogs/LinesDialog')));
const RouletteDialog = lazyWithRetry(() => (import('../dialogs/RouletteDialog')));
const JoinerWizard = lazyWithRetry(() => (import('../dialogs/WelcomeWizard')));
const SystemConfig = lazyWithRetry(() => (import('./systemConfig')));
const ItemsViewer = lazyWithRetry(() => (import('../itemViewers/SettingsViewer')));
const OwnerProfileSelection = lazyWithRetry(() => (import('./OwnerProfilesSelection')));
const MemberHomePage = lazyWithRetry(() => (import('./MemberHomePage')));
const Notifications = lazyWithRetry(() => (import('./notifications')));
const OwnerHome = lazyWithRetry(() => (import('./OwnerHome')));
const SiteBanner = lazyWithRetry(() => (import('../controls/SiteBanner')));
const SessionDetailsDialog = lazyWithRetry(() => (import('../dialogs/SessionDetailsDialog')));
const MessageDialog = lazyWithRetry(() => (import('../dialogs/MessageDialog')));
const MemberProfileDialog = lazyWithRetry(() => (import('../dialogs/MemberProfileDialog')));
const LockTaskDialog = lazyWithRetry(() => (import('../dialogs/LockTaskDialog')));
const TaskLocksViewer = lazyWithRetry(() => (import('./TaskLocksViewer')));
const OwnerProfileViewer = lazyWithRetry(() => (import('./OwnerProfileViewer')));
const MemberViewer = lazyWithRetry(() => (import('./MembersViewer')));
const PilloryViewer = lazyWithRetry(() => (import('./PilloryViewer')));
const VoteViewer = lazyWithRetry(() => (import('./VoteViewer')));
const LogOnDialog = lazyWithRetry(() => (import('../dialogs/LogOnDialog')));
const MemberProfileViewer = lazyWithRetry(() => (import('./MemberProfileViewer')));
const ExploreItemsViewer = lazyWithRetry(() => (import('./ExploreItemsViewer')));
const SettingsManager = lazyWithRetry(() => (import('./SettingsManager')));
const ConfirmDialog = lazyWithRetry(() => (import('../dialogs/ConfirmDialog')));
const RewardDialog = lazyWithRetry(() => (import('../dialogs/RewardDialog')));
const ItemCreator = lazyWithRetry(() => (import('../dialogs/ItemCreator')));
const TaskCreator = lazyWithRetry(() => (import('../dialogs/TaskCreator')));
const PilloryAdditionDialog = lazyWithRetry(() => (import('../dialogs/PilloryAdditionDialog')));
const ItemAdditionDialog = lazyWithRetry(() => (import('../dialogs/ItemAdditionDialog')));
const OwnerTransferDialog = lazyWithRetry(() => (import('../dialogs/OwnerTransferDialog')));
const GuidedHome = lazyWithRetry(() => (import('./GuidedHome')));
const AuditViewer = lazyWithRetry(() => (import('../controls/auditViewer')));
const ItemOptionsDialog = lazyWithRetry(() => (import('../dialogs/ItemOptionsDialog')));
const HelpTopicViewer = lazyWithRetry(() => (import('../dialogs/HelpTopicViewer')));
const TestRunner = lazyWithRetry(() => (import('../dialogs/TestRunner')));
const LovenseHelpDialog = lazyWithRetry(() => (import('../dialogs/LovenseHelperDialog')));
const ImportImageDbDialog = lazyWithRetry(() => (import('../dialogs/ImportImageDbDialog')));
const MetronomeDialog = lazyWithRetry(() => (import('../dialogs/MetronomeDialog')));
const controller = new SessionController();

const favicon = new Favico({
    position: 'up',
    animation: 'popFade',
    bgColor: '#dd2c00',
    textColor: '#fff0e2'
});

let notificationCount = 0;
function showFaviconBadge() {
    favicon.badge(notificationCount);
}

const oldTitle = document.title;
let focused = true;
// Capture change to visibility
const wakeVisibleSession = () => {
    focused = true;
    document.title = oldTitle;
    notificationCount = 0;
    favicon.badge(notificationCount);
    controller.wakeLiveSession();
};

document.addEventListener('visibilitychange', function () {
    // Check if tab content is visible
    if (!document.visibilityState || document.visibilityState !== 'visible') {
        focused = false;
    } else {
        wakeVisibleSession();
    }
});

window.addEventListener('focus', wakeVisibleSession);
window.addEventListener('online', wakeVisibleSession);

interface ShowProfileDialogProps extends ShowProfileProps {
    open: boolean;
}

// A custom hook that builds on useLocation to parse
// the query string for you.
function useQuery() {
    return new URLSearchParams(useLocation().search);
}

type TourStepConfig = { screenName: string, tourStep: TourStep };

const TarlingsHome = ({ cookies, notistackRef, enableInstaller }: {
    cookies: Cookies,
    notistackRef: () => (SnackbarProvider | null),
    enableInstaller: boolean,
}): JSX.Element | null => {
    const query = useQuery();
    const { enqueueSnackbar } = useSnackbar();
    const [lovenseTaskHelperItem, setLovenseTaskHelperItem] = useState<TaskBasketItem | PenaltyBasketItem>();
    const [isLoggedOn, setIsLoggedOn] = useState(false);
    const [pinnedMessage, setPinnedMessage] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [linesRequest, setLinesRequest] = useState<ILinesRequest>();
    const [proofRequest, setProofRequest] = useState<IProofRequest>();
    const [rouletteRequest, setRouletteRequest] = useState<IRouletteRequest>();
    const [dailyReportRequest, setDailyReportRequest] = useState<IReportRequest>();
    const [rewardState, setRewardState] = useState<{ settings: StatefulSettings }>();
    const [morningReportRequest, setMorningReportRequest] = useState<IReportRequest>();
    const [itemOptionsDialogState, setItemOptionsDialogState] = useState<ITaskItem>();
    const [tourState, setTourState] = useState<TourStepConfig[]>([]);
    const tourStateRef = useRef<TourStepConfig[]>(tourState);
    const [requestLockState, setRequestLockState] = useState<IRequestLockState>();
    const [acceptedTerms, setAcceptedTerms] = useState(cookies.getAll().acceptTerms === 'true');
    const [showQRCodeExplain, setShowQRCodeExplain] = useState<boolean>();
    const [showImportImageDb, setShowImportImageDb] = useState(false);
    const [metronomeDialogState, setMetronomeDialogState] = useState<{
        title?: string,
        taskKey?: string,
        audioUrl?: string,
        onStart?: (timing: { durationMs: number, intervalMs: number }) => void,
        onComplete?: () => void,
    }>();
    const [siteEffectsOverlayState, setSiteEffectsOverlayState] = useState<{
        title?: string,
        config?: SiteEffectsConfig,
        durationSec?: number,
        ownerTest?: boolean,
        onComplete?: () => void,
        onClose?: () => void,
    }>();
    const [testDialogState, setTestDialogState] = useState<{
        item: IPermissionItem | ITaskItem | IEventItem | IPenaltyItem,
        settings: Settings
    }>();
    const [taskOptionPickerState, setTaskOptionPickerState] = useState<{ settings: Settings, task: ITaskItem, callback: (option: string | undefined) => void }>();
    const [taskCreatorState, setTaskCreatorState] = useState<{ settings: StatefulSettings }>();
    const [itemCreatorState, setItemCreatorState] = useState<{ settings: Settings }>();
    const [taskPickerState, setTaskPickerState] = useState<{ tasks: ISettingsItem[], callback: (ok: boolean, task?: ISettingsItem) => void }>();

    const [editSettingItemState, setEditSettingItemState] = useState<{
        item?: ISettingsItem,
        settings?: Settings,
        cb?: (item: ISettingsItem) => void
    }>();
    const [settings, setSettings] = useState<IGeneralSettings>({} as IGeneralSettings);
    const [isLocked, setIsLocked] = useState<ILockState>();
    const [isOwned, setIsOwned] = useState(false);
    const [memberProfileDialogState, setMemberProfileDialogState] = useState<ShowProfileDialogProps>();
    const [showNewItemsDialog, setShowNewItemsDialog] = useState<boolean>(false);
    const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
    const [logonOpenState, setLogonOpenState] = useState<{
        passwordMode?: boolean,
        userNameMode?: boolean
    } | undefined>();

    const popupState = usePopupState({ variant: 'popover', popupId: 'helpTopic-popover_home', disableAutoFocus: false });
    const [sessionDialogState, setSessionDialogState] = useState<{ userid?: string }>();
    const [messageViewerState, setMessageViewerState] = useState<MessageDialogState>();
    const [requestForItemState, setRequestForItemState] = useState<BaseBasketItem>();
    const [requestedPresenceState, setRequestedPresenceState] = useState<IAttendanceRequest>();

    const [requestConfirmState, setRequestConfirmState] = useState<{ item?: ITaskItem, settings?: StatefulSettings }>();
    const popupStateRef = useRef(popupState);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    popupStateRef.current = popupState;
    const popoverTopicRef = useRef<IHelpTopic>();
    const [pilloryAdditionDetails, setPilloryAdditionDetails] = useState<{ settings: StatefulSettings, item?: IPenaltyItem }>();
    const [itemAdditionState, setItemAdditionState] = useState<IItemAdditionDialogState>();
    const [memberTransferState, setMemberTransferState] = useState<{ loan?: boolean, share?: boolean, settings: StatefulSettings }>();
    const [confirmDialogState, setConfirmDialogState] = useState<{ message: string, title: string, callback: (args: boolean) => void }>();
    const [isFatal, setIsFatal] = useState(false);
    const [changeTaskState, setChangeTaskState] = useState<IItemAdditionDialogState>();
    const [disableInstall, setDisableInstall] = useState(!enableInstaller);
    const [isStateLoaded, setIsStateLoaded] = useState(false);

    const getRouletteHandledToken = (request?: IRouletteRequest): string | undefined => {
        if (!request?.rouletteTask?.key) return undefined;
        // timesRequired is often static on the source settings item; use runtime
        // remaining count when available so each required spin is uniquely tracked.
        const runtimeRemaining = (request.rouletteTask as any).expectedCount;
        const cycle = typeof runtimeRemaining === 'number'
            ? runtimeRemaining
            : (request.rouletteTask.timesRequired ?? 1);
        return `${request.rouletteTask.key}:${cycle}`;
    };

    const refreshData = useCallback(() => {
        const userSettings = controller?.getLoggedOnUserSettings();
        if (userSettings?.isStateLoaded()) setIsStateLoaded(true);
        if (!controller || !controller.getLoggedOnUserSettings() || !controller.getLoggedOnUserSettings().isLoaded()) return; // still waiting for them
        setSettings(controller.getLoggedOnUserSettings().getGeneralSettings());
        setLinesRequest(controller.getLoggedOnUserSettings().getLinesRequest());
        setProofRequest(controller.getLoggedOnUserSettings().getProofRequest());
        setRequestedPresenceState(controller.getLoggedOnUserSettings().getRequestAttendance());
        const nextRoulette = controller.getLoggedOnUserSettings().getRouletteRequest();
        setRouletteRequest(prev => {
            if (!nextRoulette) return nextRoulette;
            const nextToken = getRouletteHandledToken(nextRoulette);
            const prevToken = getRouletteHandledToken(prev);
            // Survive hard reloads: if this task key was already handled in this tab session, keep it suppressed
            if (nextRoulette.open && nextToken && sessionStorage.getItem('rouletteHandled') === nextToken)
                return { ...nextRoulette, handled: true };
            // In-memory guard for same-session state updates
            if (prev?.handled && nextRoulette.open && prevToken === nextToken)
                return prev;
            return nextRoulette;
        });
        setDailyReportRequest(controller.getLoggedOnUserSettings().getDailyReportRequest());
        setMorningReportRequest(controller.getLoggedOnUserSettings().getMorningReportRequest());
        setIsOwned(controller.getLoggedOnUserSettings().getControl().isOwned());
        setIsLocked(controller.getLoggedOnUserSettings().isLocked());
        setRequestLockState(controller.getLoggedOnUserSettings().getRequestLockState());
    }, []);

    const refreshOnLoad = useCallback(() => {
        const controllerSettings = controller.getLoggedOnUserSettings();
        if (!isNew) {
            const settings = controllerSettings.getCurrentModeSettings();
            const onLoad = () => {
                let showNewItems = true;
                if (showNewItems && settings.hasNewItems()) {
                    Emitter.emit(ShowNewItemsDialog);
                }
                settings.unsubscribeToLoaded(onLoad);
            }
            if (settings.isLoaded()) {
                onLoad();
            } else {
                settings.subscribeToLoaded(onLoad);
            }
        }
        refreshData();
        controllerSettings.unsubscribeToLoaded(refreshData);
    }, [isNew, refreshData]);

    const showStackMessage = useCallback((args: IShowNotificationState) => {
        if (localStorage.getItem('newUI') === 'true') return; // do not show in new UI
        if (args.fatal) {
            setIsFatal(true);
        }
        const key = Math.random();
        let variant: VariantType = 'info';
        if (args.error || args.fatal) variant = 'error';
        else if (args.success) variant = 'success';
        else if (args.social) variant = 'default';
        else if (args.warning) variant = 'warning';
        enqueueSnackbar(args.message,
            {
                preventDuplicate: true,
                persist: args.persist,
                autoHideDuration: null,
                variant: variant,
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                },
                key,
                onClose: () => {
                    if (args.fatal) return;
                    notistackRef()?.closeSnackbar();
                },
                onClick: () => {
                    if (args.fatal) return;
                    notistackRef()?.closeSnackbar();
                },
            });
    }, [enqueueSnackbar, notistackRef]);

    const showMessage = useCallback((args: IShowNotificationState) => {
        if (isLoggedOn) {
            const settings = controller.getLoggedOnUserSettings();
            if (!settings || !settings.isLoaded()) return;
            const mutedNotificationTypes = settings.getControl().mutedNotificationTypes;
            if (args.notificationType && mutedNotificationTypes
                && mutedNotificationTypes.indexOf(args.notificationType) > -1)
                return;
        }

        Emitter.emit(NOTIFY_PAGE_UPDATES, args);
        args.error = args.error || args.notificationType === NotificationType.ErrorInfo;
        showStackMessage(args);
        if (!args.suppressSound && isLoggedOn && controller && controller.getLoggedOnUserSettings()
            && controller.getLoggedOnUserSettings().getIsSoundEnabled
            && controller.getLoggedOnUserSettings().getIsSoundEnabled()) {
            playSound();
        }
    }, [showStackMessage, isLoggedOn]);

    const taskPicked = useCallback((item: ISettingsItem | undefined, settings: StatefulSettings) => {
        setTaskPickerState(undefined);
        if (item === undefined) return;
        sendRequestPurchase(item as ITaskItem, settings);
        showMessage({ message: 'Purchase Request Sent', notificationType: NotificationType.MessageSent });
    }, [showMessage]);

    const proofPicked = useCallback((item: ISettingsItem | undefined, settings: StatefulSettings) => {
        setTaskPickerState(undefined);
        if (item === undefined) return;
        sendRequestProof(item as TaskBasketItem, settings);
        showMessage({ message: 'Proof Request Sent', notificationType: NotificationType.MessageSent });
    }, [showMessage]);

    const requestPurchase = useCallback((item: ITaskItem, settings: StatefulSettings) => {
        if (!item) {
            setTaskPickerState({
                tasks: settings.getTaskUnownedItems(),
                callback: (ok, task) => taskPicked(task, settings),
            });
            return;
        }
        setRequestConfirmState({ item, settings });
    }, [taskPicked]);

    const requestProof = useCallback((settings: StatefulSettings) => {
        setTaskPickerState({
            tasks: settings.getBasketInProgress(),
            callback: (ok, task) => proofPicked(task, settings),
        });
    }, [proofPicked]);

    const setInstallerDisabled = (val: boolean) => {
        setDisableInstall(val);
    };

    useEffect(() => {
        Emitter.on(DISABLE_INSTALLER, setInstallerDisabled);
        Emitter.on(NOTIFY_PAGE_UPDATES, (args: IShowNotificationState) => {
            try {
                void Notification.requestPermission().then((result) => {
                    if (result === 'granted') {
                        const notifTitle = 'Tarlings';
                        const notifBody = `${args.message}\n\nToo many notifications? \nGo to Mail and mute those required. \nThis is a ${NotificationType[args.notificationType]} message`;
                        const notifImg = 'logo192.png';
                        const options: NotificationOptions = {
                            body: notifBody,
                            icon: notifImg,
                        };
                        new Notification(notifTitle, options);
                    }
                });
            } catch (e) {
                // means they did not give access
            }
            if (focused) {
                return;
            }
            notificationCount++;
            showFaviconBadge();
            document.title = `${args.message} - ${oldTitle}`;
        });
        Emitter.on(ShowLogonDialog, args => showLogon(args as boolean));
        Emitter.on(ShowOptionPickerDialog, (task: ITaskItem, settings: StatefulSettings, callback: (option: string | undefined) => void) => setTaskOptionPickerState({ task, settings, callback }));
        Emitter.on(ShowQRCodeExplain, () => setShowQRCodeExplain(true));
        Emitter.on(ShowLovenseHelpDialog, args => setLovenseTaskHelperItem(args as TaskBasketItem | PenaltyBasketItem));
        Emitter.on(ShowMetronomeDialog, (args) => setMetronomeDialogState(args as {
            title?: string,
            taskKey?: string,
            audioUrl?: string,
            onStart?: (timing: { durationMs: number, intervalMs: number }) => void,
            onComplete?: () => void,
        }));
        Emitter.on(ShowSiteEffectsOverlay, (args) => setSiteEffectsOverlayState(args as {
            title?: string,
            config?: SiteEffectsConfig,
            durationSec?: number,
            ownerTest?: boolean,
            onComplete?: () => void,
            onClose?: () => void,
        }));
        Emitter.on(SITE_EFFECTS_TIMER_COMPLETE, () => setSiteEffectsOverlayState(undefined));
        Emitter.on(ShowNotification, (args) => {
            showMessage(args as IShowNotificationState);
        });
        Emitter.on(ShowRequestPurchase, (args1, args2) => {
            const item = args1 as any as ITaskItem;
            const settings = args2 as any as StatefulSettings;
            requestPurchase(item, settings);
        });
        Emitter.on(ShowRequestProof, (args) => {
            const settings = args as any as StatefulSettings;
            requestProof(settings);
        });
        Emitter.on(ShowHelp, async (args) => {
            if (!controller) return;
            const c = await controller.getConfig().fetchConfig();
            popoverTopicRef.current = c.help[args as any as string];
            popupStateRef.current.open(anchorRef.current);
        });

        Emitter.on(ShowRequestForItemDialog, (args) => {
            const itemRequest = args as any as BaseBasketItem;
            setRequestForItemState(itemRequest);
        });

        Emitter.on(ShowMessageDialog, (args) => {
            const messageState = args as any as MessageDialogState;
            setMessageViewerState(messageState);
        });
        Emitter.on(ShowSessionDetails, (args) => {
            const userid = args as any as string;
            setSessionDialogState({ userid });
        });
        Emitter.on(ShowProfile, (args) => {
            viewProfile(args as any as ShowProfileProps);
        });
        Emitter.on(ShowNewItemsDialog, () => {
            if (!controller || !controller.getLoggedOnUserSettings()) return;
            setShowNewItemsDialog(true);
        });
        Emitter.on(ShowSettingItemEditorDialog, (args1, args2, args3) => {
            const item = args1 as any as ISettingsItem;
            const settings = args2 as any as Settings;
            const cb = args3 as any as (i: ISettingsItem) => void;
            setEditSettingItemState({ item, settings, cb });
        });

        Emitter.on(ShowNewTaskDialog, (settings: StatefulSettings) => {
            setTaskCreatorState({ settings });
        });
        Emitter.on(ShowNewItemDialog, (settings: Settings) => {
            setItemCreatorState({ settings });
        });
        Emitter.on(ShowRewardDialog, (settings: StatefulSettings) => {
            setRewardState({ settings })
        });
        Emitter.on(ShowPilloryDialog, (settings: StatefulSettings, item: IPenaltyItem) => {
            setPilloryAdditionDetails({ settings, item })
        });
        Emitter.on(TransferOwnerDialog, (settings: StatefulSettings) => {
            setMemberTransferState({ settings })
        });
        Emitter.on(LoanMemberDialog, (settings: StatefulSettings) => {
            setMemberTransferState({ loan: true, settings })
        });
        Emitter.on(ShareMemberDialog, (settings: StatefulSettings) => {
            setMemberTransferState({ share: true, settings })
        });
        Emitter.on(ShowChangeScheduledTask, (settings: StatefulSettings, item: BaseBasketItem, onAdd?: (ok: boolean, updatedItem?: ISettingsItem) => void) => {
            setChangeTaskState({
                // countType: (item as BaseTimedBasketItem).countType ?? CountType.none,
                open: true,
                settings,
                type: item.itemType,
                item,
                itemAdditionOptions: {
                    expectedCount: (item as BaseTimedBasketItem).expectedCount ?? 0,
                    countType: (item as BaseTimedBasketItem).countType ?? CountType.none,
                    selectedCondition: (item as TaskBasketItem | PenaltyBasketItem).selectedCondition,
                    lovenseProfile: (item as TaskBasketItem | PenaltyBasketItem).lovenseProfile,
                    lovenseToyId: (item as TaskBasketItem | PenaltyBasketItem).lovenseToyId,
                    voteCount: (item as TaskBasketItem | PenaltyBasketItem).voteCount,
                },
                onAdd
            });
        });
        Emitter.on(ShowAdditionDialog, (settings: StatefulSettings,
            type: ItemTypes, item: ISettingsItem, day?: EventDay,
            itemAdditionOptions?: ItemAdditionOptions, onAdd?: (ok: boolean, updatedItem?: ISettingsItem) => void) => {
            setItemAdditionState({
                // countType: CountType.none,
                open: true,
                settings,
                type,
                item,
                day,
                itemAdditionOptions,
                onAdd
            });
        });
        Emitter.on(ShowConfirmDialog, (args: { message: string, title: string, callback: (ok: boolean) => void }) => {
            setConfirmDialogState(args)
        });
        Emitter.on(ShowOptionsDialog, (item: ITaskItem) => {
            setItemOptionsDialogState(item);
        });
        Emitter.on(ShowTestRunDialog, (settings: StatefulSettings, item: ISettingsItem) => {
            setTestDialogState({
                item: item as ITaskItem | IEventItem | IPenaltyItem,
                settings
            });
        });
        Emitter.on(ShowImportImageDbDialog, () => setShowImportImageDb(true));
        Emitter.on(ShowTourDialog, (args: { screenName: string, tourConfig: TourStep[] }) => {
            if (args.tourConfig.length === 0) return;
            const newSteps = args.tourConfig.map(tourStep => ({ screenName: args.screenName, tourStep } as TourStepConfig));
            const newArr = tourStateRef.current.concat(newSteps);
            tourStateRef.current = newArr;
            setTourState([...tourStateRef.current]);
        });
        return () => {
            Emitter.off(DISABLE_INSTALLER, setInstallerDisabled);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const performChangeTask = (result: AdditionDialogResponse | undefined) => {
        if (!changeTaskState) return;
        const { settings, onAdd } = changeTaskState;
        const item = changeTaskState.item as TaskBasketItem | PenaltyBasketItem;
        setChangeTaskState(undefined);
        if (!settings || !item || !result) return;

        // update those items you are allowed to change on the items in list
        item.countType = result.countType;
        item.expectedCount = result.expectedCount;
        item.selectedCondition = result.selectedCondition;
        if (item.itemType === ItemTypes.task) {
            (item as ITaskItem).selectedChoiceName = result.selectedChoiceName;
            (item as ITaskItem).selectedItems = result.selectedItems;
            (item as ITaskItem).selectedItemKeys = result.selectedItemsGroupKeys;
        }
        if (isTimedItem(item.countType)) item.timerTicks = item.expectedCount;
        else delete item.timerTicks;
        item.lovenseToyId = result.lovenseToyId;
        item.lovenseProfile = result.lovenseProfile;
        item.voteCount = result.voteCount;

        void controller.sendTaskChange(item, settings);
        if (onAdd) onAdd(true, item);
    }

    const handleLogOn = (newLoggedOn: boolean, isNew: boolean) => {
        setIsNew(isNew);
        setIsLoggedOn(newLoggedOn);
        if (newLoggedOn) setLogonOpenState(undefined);
        else { setLogonOpenState({}); setIsStateLoaded(false); }
        if (newLoggedOn && controller && settings) {
            const settings = controller.getLoggedOnUserSettings();
            settings.subscribeToViewChanges(refreshData);
            settings.subscribeToRequestAttendance(() => setRequestedPresenceState(settings.getRequestAttendance()));
            settings.subscribeToLoaded(refreshOnLoad);
            refreshData();
            if (isNew) {
                setShowWelcomeDialog(true);
            }
        }
    }

    const applyItemConfirmed = async (task: AdditionDialogResponse | undefined): Promise<void> => {
        setItemAdditionState(undefined);
        if (!itemAdditionState) return;
        const { settings, onAdd } = itemAdditionState;
        if (onAdd) onAdd(task !== undefined, task?.item);
        if (!task) return;

        const userItem = settings.getCurrentItem(task.item);
        if (!userItem || !isAvailableItem(userItem)) {
            await controller.getLoggedOnUserSettings().shareItem(
                settings.getControl().userid,
                settings.getControl().username,
                task.item);
        }

        if (userItem instanceof PermissionItem) {
            void settings.auditBankChange('Reward Decision', userItem.requiredCredit ?? 0)
        }

        const options: ItemAdditionOptions = {
            ...itemAdditionState.itemAdditionOptions,
            expectedCount: task.expectedCount,
            countType: task.countType,
            reason: task.reason,
            selectedCondition: task.selectedCondition,
            selectedChoiceName: task.selectedChoiceName,
            selectedItemGroupOptions: task.selectedItems,
            selectedItemGroupKeys: task.selectedItemsGroupKeys,
            ownerAdded: true,
            ownerName: controller.getLoggedOnUser().username,
            isPillory: false,
            lovenseToyId: task.lovenseToyId,
            lovenseProfile: task.lovenseProfile,
            voteCount: task.voteCount,

        };

        void controller.sendAddRequest([{
            item: task.item, itemAdditionOptions: options,
            updateType: PendingUpdateType.add, date: formatValidDate(task.date, 'dd MMM yyyy HH:mm')
        }], settings);
        showMessage({ message: `${task.item.name} added to ${settings.getControl().username} session`, notificationType: NotificationType.ScheduleInfo });
    }

    const saveOptionsForItem = (isOK: boolean, item?: ITaskItem): void => {
        setItemOptionsDialogState(undefined);
        if (!isOK || !item) return;
        const settings = controller.getLoggedOnUserSettings();
        void settings.editOwnedItems(item, true);
    }

    const itemCreated = (newItem?: Item) => {
        setItemCreatorState(undefined);
        if (!itemCreatorState) return;
        const { settings } = itemCreatorState;
        if (!settings || !newItem) return;
        settings.itemCreated(newItem);
    }

    const taskCreated = (newTask?: Task) => {
        setTaskCreatorState(undefined);
        if (!taskCreatorState) return;
        const { settings } = taskCreatorState;
        if (!settings || !newTask) return;
        settings.taskCreated(newTask);
    }

    useEffect(() => {
        controller.initialise(cookies, showMessage);
        controller.subscribeToLogOn(handleLogOn);
    });

    const location = useLocation();

    const closeWelcomeDialog = (): void => {
        setShowWelcomeDialog(false);
        // now we can set up the session based on how they want it
        void controller?.createScheduler();
    }

    const submitLines = (task: ITaskItem | IPenaltyItem,
        lines: string,
        line: string,
        expectedCount: number,
        mistakes: number,
        stats: ITypingStats): void => {
        if (!controller || !linesRequest) return;
        const hasPenaltyForMistakes = (task && task.penaltyKeys && task.penaltyKeys.length > 0) ?? false;
        const linesPenalty = hasPenaltyForMistakes && task.penaltyKeys && task.penaltyKeys[0];
        const settings = controller.getLoggedOnUserSettings();
        const penalty = linesPenalty ? settings.getPenalty(linesPenalty) : undefined;
        void controller.sendLines(lines,
            line,
            expectedCount,
            mistakes,
            linesRequest.item.key,
            !(hasPenaltyForMistakes
                && penalty
                && PenaltyItem.IsLinesPenalty(penalty)),
            stats);
        if (penalty && mistakes > 0) {
            if (task.sendMistakesToPillory) {
                settings.createPilloryTopic({
                    addCountLimits: true,
                    countType: penalty.countType,
                    customCondition: false,
                    date: addDays(new Date(), 1),
                    item: penalty,
                    reason: 'Mistakes during lines',
                    voteCount: task.mistakeCount ?? getDefaultCountPerMistake(task),
                    votable: true,
                });
            } else {
                const result = settings.canAddTask(penalty);
                if (!result.isOk) {
                    settings.auditUser(new AuditMessage(
                        AuditActionTypes.Penalty,
                        penalty.name,
                        `Unable to add Penalty ${penalty.name} to task list - ${result.reason}`,
                        TraceLevel.error,
                    ));
                }
                const mistakeCost = task.mistakeCount ?? getDefaultCountPerMistake(task);
                const totalMistakeCount = mistakeCost * mistakes;
                let expectedCount = totalMistakeCount;
                if (isTimedItem(penalty.countType)) {
                    expectedCount = getCountMinutes(totalMistakeCount, penalty.countType);
                }

                const basketItems = settings.createBasketItems(penalty,
                    settings.getIsWeekend,
                    `Penalty ${penalty.name} added for ${mistakes} mistakes made during lines. Count set to ${convertToCountTypeString(totalMistakeCount, penalty.countType)}`,
                    new Date(),
                    { expectedCount, reason: 'Penalty added for mistakes made during lines.' });

                if (basketItems && basketItems.length) basketItems.forEach(basketItem => settings.updateTaskState(basketItem));
                showMessage({ message: 'Penalty Added', notificationType: NotificationType.ScheduleInfo });
                settings.auditUser(new AuditMessage(
                    AuditActionTypes.Penalty,
                    penalty.name,
                    `Penalty ${penalty.name} added for ${mistakes} mistakes made during lines. Count set to  ${convertToCountTypeString(totalMistakeCount, penalty.countType)}`,
                    TraceLevel.info,
                ));
            }
        }
    }

    const submitRouletteRequest = (item: string | ITaskItem | IPenaltyItem | ITaskVoteConfig, options?: ItemAdditionOptions): void => {
        if (!controller || !rouletteRequest) return;
        void controller.setRouletteResult(item, rouletteRequest, options);
    }

    const submitReview = (reviewText: string): void => {
        if (!controller) return;
        void controller.saveDailyReport(reviewText);
    }

    const submitMorningReport = (reviewText: string): void => {
        if (!controller) return;
        controller.saveMorningReport(reviewText);
    }

    const viewProfile = (props: ShowProfileProps) => {
        setMemberProfileDialogState({
            ...props,
            open: true,
        });
    }

    const pinMessage = () => {
        setPinnedMessage(true);
    }
    const showPinnedMessage = () => {
        setPinnedMessage(false);
    }

    const pilloryAdditionConfirmed = (itemToAdd: AdditionDialogResponse | undefined): void => {
        setPilloryAdditionDetails(undefined);
        if (!pilloryAdditionDetails) return;
        const settings = pilloryAdditionDetails.settings;
        if (!itemToAdd) return;
        settings.createPilloryTopic(itemToAdd);
    }

    const onProfileClose = () => {
        setMemberProfileDialogState(undefined);
    }

    const performTransferOwner = async (owner: ControlRecord | undefined): Promise<void> => {
        setMemberTransferState(undefined);
        if (!memberTransferState) return;
        const { loan, share, settings } = memberTransferState;
        if (owner) {
            if (share) {
                settings.acceptSessionRequest(owner.userid, owner.username, false, true);
                return;
            }
            if (!loan) await settings.unownSessionForUser(settings.getControl(), true);
            settings.acceptSessionRequest(owner.userid, owner.username, loan ?? false, false);
        }
    }

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const showLogon = (changeUserName: boolean) => {
        setLogonOpenState({ passwordMode: !changeUserName, userNameMode: changeUserName });
    }

    const sendUserMessage = (message: string, toUsername: string, subject: string, toUserid?: string) => {
        if (!messageViewerState) return;
        const { sendUserMessage: innerCallback } = messageViewerState;
        setMessageViewerState(undefined);
        if (!controller || !controller.getLoggedOnUserSettings()) return;
        if (!message || !subject) return;
        const toUser = findUser(toUsername, toUserid,
            controller.getDatabase().getSystemUser(),
            controller.getLoggedOnUserSettings().getOwnedSessions());
        if (!toUser) return;
        if (innerCallback) innerCallback(message, toUser.username, subject, toUser.userid);
        else sendMessageToMember(MessageType.MemberMessage, controller.getLoggedOnUserSettings(), message, subject, [toUser]);
    }

    const closeMessage = () => {
        setMessageViewerState(undefined);
    }

    const closeTour = () => {
        if (tourStateRef.current.length > 0) {
            tourStateRef.current.forEach(removed => controller.updateTourState(removed.screenName, removed.tourStep));
        }
        setTourState([]);
    };


    type ClickProps = {
        currentStep: number
        meta: string
    }


    return (
        <Suspense fallback={<LazyLoader
            status="Loading Screen..."
            delay={100} />}>
            <ControllerContext.Provider value={controller}>
                <SelectedSettingsContext.Provider value={isLoggedOn ? controller.getLoggedOnUserSettings() : {} as StatefulSettings}>
                    <Box>
                        {!isLoggedOn &&
                            <LoginScreen query={query} showMessage={showMessage} cookies={cookies} />
                        }
                        {isLoggedOn && isFatal && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '100vh',
                                    p: 3,
                                    gap: 2,
                                    bgcolor: 'background.default',
                                }}
                            >
                                <Typography variant="h5" color="error" fontWeight={600}>
                                    A fatal error has occurred
                                </Typography>
                                <Typography variant="body1" color="text.secondary" textAlign="center">
                                    Please reload the page. If the problem persists, contact support.
                                </Typography>
                                <Button variant="contained" color="error" onClick={() => window.location.reload()}>
                                    Reload
                                </Button>
                            </Box>
                        )}
                        {isLoggedOn && !isFatal && (
                            <ErrorBoundary location={location} controller={controller}>
                                <Box
                                    id="outer-container"
                                    sx={{ bgcolor: settings?.backgroundColor || 'background.default' }}
                                    ref={anchorRef}
                                >
                                    {pinnedMessage && (
                                        <Fab
                                            variant="extended"
                                            sx={{
                                                position: 'fixed',
                                                top: { xs: 56, sm: 64 },
                                                right: { xs: 8, sm: 220 },
                                                zIndex: 1200,
                                                bgcolor: 'success.main',
                                                color: '#fff',
                                                borderRadius: 0,
                                                textTransform: 'capitalize',
                                                '&:hover': { bgcolor: '#485ed7' },
                                            }}
                                            disableTouchRipple
                                            disableRipple
                                            disableFocusRipple
                                            onClick={showPinnedMessage}
                                            aria-label="Restore Message"
                                        >
                                            <span>{messageViewerState?.subject}</span>
                                            <OpenInNew sx={{ ml: 1 }} />
                                        </Fab>
                                    )}
                                    <SiteBanner disableInstall={disableInstall} />
                                    <Routes>
                                        <Route
                                            path={notifications_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <Notifications
                                                    initialMessageId={query.get('command') ?? undefined}
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={settings_page}
                                            element={<ProtectedPage
                                                authCheck={() => !isLocked && !isOwned}
                                            >
                                                <ItemsViewer
                                                    overrideSettings={controller.getLoggedOnUserSettings().isAdmin() ? controller.getLoggedOnUserOwnerProfileSettings() : undefined}
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={settingsManager_page}
                                            element={<ProtectedPage
                                                authCheck={() => controller
                                                    && controller.getLoggedOnUserSettings().isSystemLoggedOn()}
                                            >
                                                <SettingsManager
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={welcome_page}
                                            element={<ProtectedPage
                                                authCheck={() => controller.getLoggedOnUserSettings().getCurrentCommunityProfile().role !== ProfileRole.Member}
                                            >
                                                <GuidedHome />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={lovense_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <LovenseConfiguration
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={owners_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <OwnerProfileSelection
                                                    ownerId={query.get('owner')}
                                                    key={query.get('owner')}
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={items_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <ExploreItemsViewer
                                                    command={query.get('command') ?? undefined}
                                                    key={query.get('command')}
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={userAudit_page}
                                            element={<ProtectedPage
                                                authCheck={() => controller
                                                    && controller.getLoggedOnUserSettings().canEditSettings()}
                                            >
                                                <AuditViewer showLogs />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={members_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <MemberViewer
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={sessions_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <ActiveSessionsViewer
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={locks_page}
                                            element={<ProtectedPage
                                                authCheck={() => controller.getLoggedOnUserSettings().isAdmin()}
                                            >
                                                <TaskLocksViewer
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={pillory_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <PilloryViewer
                                                    command={query.get('command') ?? undefined}
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={editProfile_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                {isLoggedOn && (controller.getLoggedOnUserSettings().getCurrentCommunityProfile().role === ProfileRole.Member
                                                    || (controller.getLoggedOnUserSettings().getCurrentCommunityProfile().role === ProfileRole.Switch
                                                        && !controller.getLoggedOnUserSettings().isAdmin()))
                                                    && !controller.getLoggedOnUserSettings().isSystemLoggedOn()
                                                    && <MemberProfileViewer
                                                        command={query.get('command') ?? undefined}
                                                    />
                                                }
                                                {isLoggedOn && (controller.getLoggedOnUserSettings().getCurrentCommunityProfile().role === ProfileRole.Owner
                                                    || (controller.getLoggedOnUserSettings().getCurrentCommunityProfile().role === ProfileRole.Switch && controller.getLoggedOnUserSettings().isAdmin())
                                                    || controller.getLoggedOnUserSettings().isSystemLoggedOn())
                                                    && <OwnerProfileViewer
                                                        command={query.get('command') ?? undefined}
                                                    />
                                                }
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={votes_page}
                                            element={<ProtectedPage
                                                authCheck={() => true}
                                            >
                                                <VoteViewer
                                                    command={query.get('command') ?? undefined}
                                                />
                                            </ProtectedPage>}
                                        />
                                        <Route
                                            path={config_page}
                                            element={<ProtectedPage
                                                authCheck={() => controller
                                                    && controller.getLoggedOnUserSettings().isSystemLoggedOn()}
                                            >
                                                <SystemConfig
                                                />
                                            </ProtectedPage>}
                                        />

                                        {isLoggedOn && !showWelcomeDialog && isStateLoaded && controller.getLoggedOnUserSettings().isAdmin()
                                            && (location.pathname === home_page || location.pathname === root_page)
                                            && <Route
                                                path={location.pathname}
                                                element={<ProtectedPage
                                                    authCheck={() => true}
                                                >
                                                    <OwnerHome
                                                    />
                                                </ProtectedPage>}
                                            />}

                                        {isLoggedOn && !showWelcomeDialog && isStateLoaded
                                            && !controller.getLoggedOnUserSettings().isAdmin()
                                            && (location.pathname === home_page || location.pathname === root_page)
                                            && <Route
                                                path={location.pathname}
                                                element={<ProtectedPage
                                                    authCheck={() => true}
                                                >
                                                    <MemberHomePage
                                                        command={query.get('command') ?? undefined}
                                                        version={query.get('version') ?? undefined}
                                                    />
                                                </ProtectedPage>}
                                            />}
                                    </Routes>
                                </Box>
                            </ErrorBoundary>
                        )
                        }
                        {itemOptionsDialogState && <ItemOptionsDialog
                            taskItem={itemOptionsDialogState}
                            onSave={saveOptionsForItem}
                        />}
                        {
                            logonOpenState && (<LogOnDialog
                                cookies={cookies}
                                showMessage={showMessage}
                                changePasswordMode={logonOpenState.passwordMode}
                                changeUserName={logonOpenState.userNameMode}
                                onChangeSuccess={() => setLogonOpenState(undefined)}
                                onCancel={() => setLogonOpenState(undefined)}
                            />)
                        }

                        {
                            isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin() && linesRequest && <LinesDialog
                                requiredCount={linesRequest.count}
                                requiredLine={linesRequest.line || (controller.getLoggedOnUserSettings().getRandomLine(linesRequest) ?? '')}
                                submitCallback={submitLines}
                                lineItem={linesRequest.item}
                                editorSettings={controller.getLoggedOnUserSettings()}
                            />
                        }

                        {
                            isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && proofRequest
                            && proofRequest.items[0]
                            && proofRequest.open
                            && <ProofRequestCapture
                                task={proofRequest.items[0]}
                                open={true}
                                proofCode={proofRequest.items[0].proofCode}
                                callback={() => controller.getLoggedOnUserSettings().updateProofDialog(proofRequest.items[0], false)}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.quiz
                            && <QuizDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                quiz={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IQuizGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }
                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.wordsearch
                            && <WordSearchDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                wordsearch={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IWordSearchGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.cardFlip
                            && <CardFlipDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as ICardFlipGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.higherlower
                            && <HigherOrLowerDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IHigherOrLowerGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }


                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.memoryNumbers
                            && <MemoryNumbersDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IMemoryNumbersGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }


                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.minefield
                            && <MinefieldDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IMinefieldGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.patternRecall
                            && <PatternRecallDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IPatternRecallGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.battleShips
                            && <BattleShipsDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IBattleShipsGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.ticTacToe
                            && <TicTacToeDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as ITicTacToeGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.blackJack
                            && <BlackJackDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IBlackJackGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.rockPaperScissors
                            && <RockPaperScissorsDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IRockPaperScissorsGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.fruitMachine
                            && <FruitMachineDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IFruitMachineGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.roulette
                            && <CrapsDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as IRouletteGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin()
                            && controller.getLoggedOnUserSettings().getCurrentGame()
                            && controller.getLoggedOnUserSettings().getCurrentGame()?.game?.gameType === GameTypes.snakesAndLadders
                            && <SnakesAndLaddersDialog
                                onClose={(result: IGameResult) => controller.getLoggedOnUserSettings().setGameResult(result)}
                                game={controller.getLoggedOnUserSettings().getCurrentGame()?.game as ISnakesAndLaddersGame}
                                penaltyKeys={controller.getLoggedOnUserSettings().getCurrentGame()?.penaltyKeys}
                            />
                        }

                        {
                            isLoggedOn
                            && rouletteRequest
                            && rouletteRequest.open
                            && !rouletteRequest.handled
                            && <RouletteDialog
                                {...rouletteRequest}
                                submitCallback={(item: string | ITaskItem | IPenaltyItem | ITaskVoteConfig, options?: ItemAdditionOptions) => {
                                    const handledToken = getRouletteHandledToken(rouletteRequest);
                                    if (handledToken)
                                        sessionStorage.setItem('rouletteHandled', handledToken);
                                    setRouletteRequest(r => r ? { ...r, handled: true } : r);
                                    if (item) submitRouletteRequest(item, options);
                                }}
                            />
                        }

                        {
                            isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin() && dailyReportRequest && dailyReportRequest.open && <ReportDialog
                                title={`Enter a report for the day for our review. It must exceed ${dailyReportRequest?.count} words.`}
                                requiredCount={dailyReportRequest?.count}
                                submitCallback={submitReview}
                                configCache={controller.getConfig()}
                            />
                        }
                        {
                            isLoggedOn && !controller.getLoggedOnUserSettings().isAdmin() && morningReportRequest && morningReportRequest.open && <ReportDialog
                                title={`Enter a morning message for your owner. It must exceed ${morningReportRequest?.count} words.`}
                                requiredCount={morningReportRequest?.count}
                                submitCallback={submitMorningReport}
                                configCache={controller.getConfig()}
                            />
                        }
                        {
                            showWelcomeDialog && <JoinerWizard
                                callback={closeWelcomeDialog}
                                isNew
                            />
                        }
                        {
                            sessionDialogState && sessionDialogState.userid && (
                                <SessionDetailsDialog
                                    open
                                    callback={() => setSessionDialogState(undefined)}
                                    editorSettings={controller.getUserSettings(sessionDialogState.userid)}
                                />
                            )
                        }
                        {
                            showNewItemsDialog && (
                                <NewItemViewerDialog
                                    settings={controller.getLoggedOnUserSettings().getCurrentModeSettings()}
                                    callback={() => setShowNewItemsDialog(false)}
                                />
                            )
                        }

                        {
                            requestForItemState && (
                                <RequestForItemDialog
                                    item={requestForItemState}
                                    settings={controller.getLoggedOnUserSettings().getCurrentModeSettings()}
                                    callback={() => setRequestForItemState(undefined)}
                                />
                            )
                        }


                        {
                            requestedPresenceState && (
                                <RequestedPresenceDialog
                                    request={requestedPresenceState}
                                    settings={controller.getLoggedOnUserSettings()}
                                    callback={() => setRequestedPresenceState(undefined)}
                                />
                            )
                        }

                        {
                            !pinnedMessage && messageViewerState && <MessageDialog
                                {...messageViewerState}
                                fromUser={controller.getUserControl(messageViewerState.fromUserid as string)}
                                sendMessage={sendUserMessage}
                                closeMessage={closeMessage}
                                hideMessage={pinMessage}
                                updateMessage={(message: string) => messageViewerState.message = message}
                            />
                        }
                        {editSettingItemState
                            && editSettingItemState.item
                            && editSettingItemState.settings
                            && <SelectedSettingsContext.Provider value={editSettingItemState?.settings as StatefulSettings}>

                                {(editSettingItemState.item.itemType === ItemTypes.task
                                    || editSettingItemState.item.itemType === ItemTypes.penalty)
                                    && <TaskBasedSettingItemEditorDialog
                                        editItem={editSettingItemState.item as TaskItem | PenaltyItem}
                                        dialogOpenCallback={() => setEditSettingItemState(undefined)}
                                        saveCallback={editSettingItemState.cb}
                                        overrideSettings={editSettingItemState.settings}
                                    />
                                }

                                {
                                    editSettingItemState.item.itemType === ItemTypes.position
                                    && <PositionItemViewer
                                        editItem={editSettingItemState.item as ISettingsItem}
                                        dialogOpenCallback={() => setEditSettingItemState(undefined)}
                                        saveCallback={editSettingItemState.cb}
                                        overrideSettings={editSettingItemState.settings}
                                    />
                                }

                                {
                                    (editSettingItemState.item.itemType === ItemTypes.event
                                        || editSettingItemState.item.itemType === ItemTypes.permission)
                                    && <EventBasedSettingItemEditorDialog
                                        editItem={editSettingItemState.item as IEventItem | IPermissionItem}
                                        dialogOpenCallback={() => setEditSettingItemState(undefined)}
                                        saveCallback={editSettingItemState.cb}
                                        overrideSettings={editSettingItemState.settings}
                                    />
                                }
                            </SelectedSettingsContext.Provider>}
                        {
                            requestLockState && (requestLockState.item || requestLockState.result) &&
                            <LockTaskDialog
                                open
                                requestLockState={requestLockState}
                                callback={(ok: boolean, state: IRequestLockState) => {
                                    controller.setRequestLockComplete(state);
                                    setRequestLockState(undefined);
                                }}
                            />
                        }
                        {
                            memberProfileDialogState
                            && memberProfileDialogState.profile && (
                                <MemberProfileDialog
                                    profile={memberProfileDialogState.profile}
                                    ownerProfile={memberProfileDialogState.ownerProfile}
                                    close={onProfileClose}
                                    ownedSessions={memberProfileDialogState.ownedSessions}
                                    topMember={memberProfileDialogState.isTopMember}
                                    bottomMember={memberProfileDialogState.isBottomMember}
                                    topOwner={memberProfileDialogState.isTopOwner}

                                />
                            )
                        }

                        {confirmDialogState && <ConfirmDialog
                            message={confirmDialogState.title}
                            content={confirmDialogState.message}
                            open
                            callback={(ok: boolean) => {
                                setConfirmDialogState(undefined);
                                confirmDialogState.callback(ok);
                            }} />}
                        {
                            requestConfirmState && <ConfirmDialog open message={`Request Purchase of ${requestConfirmState.item?.name}`} callback={() => {
                                if (requestConfirmState.settings && requestConfirmState.item) {
                                    setRequestConfirmState(undefined);
                                    taskPicked(requestConfirmState.item, requestConfirmState.settings);
                                }
                            }}
                            />
                        }

                        {
                            rewardState && (
                                <RewardDialog
                                    open
                                    settings={rewardState.settings}
                                    callback={(perk: IReward | undefined, reason: string | undefined) => {
                                        setRewardState(undefined);
                                        if (perk) controller.reward(perk, reason, rewardState.settings);
                                    }}
                                />)
                        }

                        {
                            itemCreatorState && (<ItemCreator
                                settings={itemCreatorState.settings}
                                callback={itemCreated}
                            />)
                        }


                        {
                            taskCreatorState && (<TaskCreator
                                settings={taskCreatorState.settings}
                                callback={taskCreated}
                            />)
                        }
                        {
                            testDialogState && <TestRunner
                                editorSettings={testDialogState.settings}
                                item={testDialogState.item}
                                callback={(): void => {
                                    setTestDialogState(undefined);
                                }}
                            />
                        }
                        {
                            showImportImageDb && <ImportImageDbDialog
                                open={showImportImageDb}
                                settings={controller.getLoggedOnUserSettings()}
                                onClose={() => setShowImportImageDb(false)}
                            />
                        }

                        {
                            taskPickerState && (<TaskPicker
                                tasks={taskPickerState.tasks}
                                callback={taskPickerState.callback}
                            />)
                        }

                        {
                            taskOptionPickerState && (<TaskOptionPicker
                                options={taskOptionPickerState.task?.options as string[]}
                                callback={(isOk: boolean, option: string | undefined) => {
                                    setTaskOptionPickerState(undefined);
                                    taskOptionPickerState.callback(isOk ? option : undefined);
                                }}
                            />)
                        }

                        {
                            pilloryAdditionDetails
                            && pilloryAdditionDetails.settings && (<PilloryAdditionDialog
                                open
                                pilloryItem={pilloryAdditionDetails.item}
                                settings={pilloryAdditionDetails.settings}
                                callback={pilloryAdditionConfirmed}
                            />)
                        }

                        {
                            itemAdditionState && itemAdditionState.open && (<ItemAdditionDialog
                                open
                                settings={itemAdditionState.settings}
                                callback={applyItemConfirmed}
                                item={itemAdditionState.item}
                                itemType={itemAdditionState.type}
                                day={itemAdditionState.day}
                                itemAdditionOptions={itemAdditionState.itemAdditionOptions}
                            />)
                        }

                        {lovenseTaskHelperItem && <LovenseHelpDialog
                            item={lovenseTaskHelperItem}
                            callback={(cancelled: boolean) => {
                                setLovenseTaskHelperItem(undefined);
                                // let's replay it and see if it works this time.
                                if (!cancelled) controller.handlePlayItem(lovenseTaskHelperItem);
                            }}
                        />}

                        {metronomeDialogState && (
                            <Suspense fallback={null}>
                                <MetronomeDialog
                                    title={metronomeDialogState.title}
                                    taskKey={metronomeDialogState.taskKey}
                                    audioUrl={metronomeDialogState.audioUrl}
                                    tasks={controller.getLoggedOnUserSettings()?.getTasks?.(t => !!t.name) ?? []}
                                    onClose={() => setMetronomeDialogState(undefined)}
                                    onStart={metronomeDialogState.onStart}
                                    onComplete={metronomeDialogState.onComplete}
                                />
                            </Suspense>
                        )}

                        {siteEffectsOverlayState && (
                            <SiteEffectsOverlaySession
                                open
                                title={siteEffectsOverlayState.title}
                                config={siteEffectsOverlayState.config ?? defaultSiteEffectsConfig}
                                durationSec={siteEffectsOverlayState.durationSec}
                                ownerTest={siteEffectsOverlayState.ownerTest}
                                onClose={() => {
                                    siteEffectsOverlayState.onClose?.();
                                    setSiteEffectsOverlayState(undefined);
                                }}
                                onComplete={() => {
                                    siteEffectsOverlayState.onComplete?.();
                                    setSiteEffectsOverlayState(undefined);
                                }}
                            />
                        )}

                        {changeTaskState && <ItemAdditionDialog
                            open
                            isUpdate
                            settings={changeTaskState.settings}
                            callback={performChangeTask}
                            item={changeTaskState.item}
                            itemType={changeTaskState.type}
                            hideCustomisations={false}
                            day={changeTaskState.day}
                            itemAdditionOptions={changeTaskState.itemAdditionOptions}
                        />}

                        {
                            memberTransferState && <OwnerTransferDialog
                                open
                                loan={memberTransferState.loan ?? false}
                                share={memberTransferState.share ?? false}
                                callback={performTransferOwner}
                                allOwners={controller.getLoggedOnUserSettings().getCommunityProfiles().filter(p => p.role !== ProfileRole.Member)}
                            />
                        }
                        {
                            showQRCodeExplain && <ConfirmDialog
                                callback={() => {
                                    setShowQRCodeExplain(undefined);
                                    controller.getLoggedOnUserSettings().updateControlAttributes({ lovenseSync: true });
                                }}
                                message='Authorise Tarlings'
                                content={`A QR Code should have opened in a new tab. If you have popups blocked, please disable the blocker and try again. When you have the QR code, Go to your Lovense app and in top right click + symbol and choose Scan QR Code.
                        Even if you have already done this, Lovense requires you to refresh the QR Code on each control.
                        This will link together Tarlings and your Toy for your session.
                        You can save the code image and upload it in the 'scan qr code' option in the App.
                        Remember to have the app open and "keep screen busy" enabled for your entire task time`}
                                open
                                hideCancel
                            />
                        }
                        {popoverTopicRef.current && popupState.isOpen && <HelpTopicViewer popupState={popupState} popoverTopic={popoverTopicRef.current} />}
                    </Box>
                    {tourState.length > 0 && <Tour
                        onRequestClose={closeTour}
                        disableInteraction={false}
                        onClickMask={(props: ClickProps) => {
                            controller.updateTourState(tourState[props.currentStep].screenName, tourState[props.currentStep].tourStep);

                        }}
                        /// merges all the elements from all the screens in the current tour into a single list
                        steps={Array.from(tourState.map(tour => tour.tourStep))}
                        isOpen
                        maskClassName="maskSpotlight"
                        className="helper"
                        rounded={5}
                        accentColor={'#5cb7b7'}
                    />}
                    <Dialog
                        open={!acceptedTerms}
                        maxWidth="sm"
                        fullWidth
                        fullScreen={isMobile}
                        BackdropProps={{ style: { opacity: 0.75, backgroundColor: '#000' } }}
                        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
                    >
                        <DialogTitle>
                            <Typography variant="h6" fontWeight={600}>Our Terms and Conditions</Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            <DialogContentText component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box>
                                    <Typography variant="body2" component="span" fontWeight={700}>Cookies: </Typography>
                                    <Typography variant="body2" component="span">
                                        We use cookies to collect your username and password only for this log on screen — if you <strong>do not agree</strong> to this do not accept our terms.
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" component="span" fontWeight={700}>Age: </Typography>
                                    <Typography variant="body2" component="span">
                                        You verify that you are at least 18 years of age and it is legal for you to view this content in your country. If you are underage, please close this site.
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" component="span" fontWeight={700}>Responsibilities: </Typography>
                                    <Typography variant="body2" component="span">
                                        Any participation with this site, or with any members, is your responsibility. We will not be held responsible for any act, injury or outcome as a result of using this site. By continuing you agree to these terms.
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" component="span" fontWeight={700}>GDPR: </Typography>
                                    <Typography variant="body2" component="span">
                                        We do NOT collect, hold or share any personal data in convention with GDPR. Please do not store any personal data in your profile — it will be removed.
                                    </Typography>
                                </Box>
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                            <Button variant="outlined" onClick={() => history.back()}>Go Back</Button>
                            <Button variant="contained" onClick={() => {
                                cookies.set('acceptTerms', true);
                                setAcceptedTerms(true);
                            }}>Agree &amp; Continue</Button>
                        </DialogActions>
                    </Dialog>
                    {import.meta.env.DEV && <MemoryProfiler />}
                </SelectedSettingsContext.Provider>
            </ControllerContext.Provider>
        </Suspense>
    );
}

export default withCookies(memo(TarlingsHome, () => true));